// utilities/projectImporter.js
export const importCompleteProject = (file, onSuccess, onError) => {
  if (!file) {
    onError('لا يوجد ملف محدد');
    return;
  }

  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const importedData = JSON.parse(e.target.result);
      
      // التحقق من نوع الملف المستورد
      const importResult = detectAndImportProject(importedData);
      
      if (importResult.success) {
        onSuccess(importResult);
      } else {
        onError(importResult.message);
      }
      
    } catch (error) {
      console.error('خطأ في استيراد المشروع:', error);
      onError('ملف غير صحيح أو تالف');
    }
  };
  
  reader.onerror = () => {
    onError('فشل في قراءة الملف');
  };
  
  reader.readAsText(file);
};

// كشف نوع المشروع واستيراده
const detectAndImportProject = (data) => {
  try {
    // التحقق من النسخة الكاملة الجديدة
    if (data.exportInfo && data.projectData && data.exportInfo.version === '2.0') {
      return importCompleteProjectV2(data);
    }
    
    // التحقق من نسخة استيراد المشروع القديمة
    if (data.projectInfo && data.projectData) {
      return importLegacyProject(data);
    }
    
    // التحقق من تصدير صفحة واحدة
    if (data.pageData && data.pageId) {
      return importSinglePage(data);
    }
    
    // التحقق من النسخة السريعة أو البيانات المباشرة
    if (data.pagesById || data.elements) {
      return importQuickProject(data);
    }
    
    return {
      success: false,
      message: 'تنسيق الملف غير مدعوم'
    };
    
  } catch (error) {
    return {
      success: false,
      message: `خطأ في معالجة البيانات: ${error.message}`
    };
  }
};

// استيراد المشروع الكامل (النسخة 2.0)
const importCompleteProjectV2 = (data) => {
  try {
    const newProjectId = `imported_${Date.now()}`;
    const projectInfo = {
      id: newProjectId,
      name: `${data.projectInfo.name} (مستورد)`,
      description: data.projectInfo.description || '',
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    // تحضير بيانات المشروع
    const projectData = {
      pagesById: data.projectData.pagesById || data.projectData.pages || {},
      pageOrder: data.projectData.pageOrder || ['floor', 'ceiling'],
      activePageId: data.projectData.activePageId || 'floor',
      snapToGrid: data.projectData.settings?.snapToGrid ?? data.projectData.snapToGrid ?? true,
      gridSize: data.projectData.settings?.gridSize || data.projectData.gridSize || 5,
      selectedAngleDirection: data.projectData.settings?.selectedAngleDirection || data.projectData.selectedAngleDirection || 'horizontal_right_up',
      projectInfo: data.projectData.projectInfo || data.projectData.projectDetails || {},
      devices: data.projectData.devices || [],
      deviceNote: data.projectData.deviceNote || '',
      createdAt: projectInfo.createdAt,
      lastModified: projectInfo.lastModified
    };

    // حفظ المشروع
    saveImportedProject(newProjectId, projectInfo, projectData);

    return {
      success: true,
      message: 'تم استيراد المشروع الكامل بنجاح',
      projectId: newProjectId,
      projectInfo: projectInfo,
      projectData: projectData,
      type: 'complete'
    };

  } catch (error) {
    return {
      success: false,
      message: `فشل في استيراد المشروع الكامل: ${error.message}`
    };
  }
};

// استيراد المشروع القديم
const importLegacyProject = (data) => {
  try {
    const newProjectId = `imported_${Date.now()}`;
    const projectInfo = {
      id: newProjectId,
      name: `${data.projectInfo.name} (مستورد - قديم)`,
      description: data.projectInfo.description || '',
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    // تحويل البيانات القديمة إلى التنسيق الجديد
    const projectData = convertLegacyData(data.projectData);
    projectData.createdAt = projectInfo.createdAt;
    projectData.lastModified = projectInfo.lastModified;

    saveImportedProject(newProjectId, projectInfo, projectData);

    return {
      success: true,
      message: 'تم استيراد المشروع القديم وتحويله بنجاح',
      projectId: newProjectId,
      projectInfo: projectInfo,
      projectData: projectData,
      type: 'legacy'
    };

  } catch (error) {
    return {
      success: false,
      message: `فشل في استيراد المشروع القديم: ${error.message}`
    };
  }
};

// استيراد صفحة واحدة
const importSinglePage = (data) => {
  try {
    const newProjectId = `imported_page_${Date.now()}`;
    const projectInfo = {
      id: newProjectId,
      name: `${data.pageName} (صفحة مستوردة)`,
      description: 'مشروع تم إنشاؤه من صفحة مستوردة',
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    // إنشاء مشروع جديد مع الصفحة المستوردة
    const projectData = createDefaultProject();
    projectData.pagesById[data.pageId] = data.pageData;
    projectData.activePageId = data.pageId;
    
    if (!projectData.pageOrder.includes(data.pageId)) {
      projectData.pageOrder.push(data.pageId);
    }

    saveImportedProject(newProjectId, projectInfo, projectData);

    return {
      success: true,
      message: `تم استيراد الصفحة "${data.pageName}" كمشروع جديد`,
      projectId: newProjectId,
      projectInfo: projectInfo,
      projectData: projectData,
      type: 'single-page'
    };

  } catch (error) {
    return {
      success: false,
      message: `فشل في استيراد الصفحة: ${error.message}`
    };
  }
};

// استيراد النسخة السريعة
const importQuickProject = (data) => {
  try {
    const newProjectId = `imported_quick_${Date.now()}`;
    const projectInfo = {
      id: newProjectId,
      name: 'مشروع مستورد (سريع)',
      description: 'مشروع تم استيراده من ملف بيانات سريع',
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    // إما بيانات مشروع كاملة أو بيانات عناصر فقط
    let projectData;
    
    if (data.pagesById) {
      // بيانات مشروع كاملة
      projectData = {
        ...createDefaultProject(),
        ...data
      };
    } else if (data.elements) {
      // بيانات عناصر فقط - تحويل إلى صيغة مشروع
      projectData = createDefaultProject();
      projectData.pagesById.floor.elements = data.elements;
      
      if (data.layers) {
        projectData.pagesById.floor.layers = data.layers;
      }
    } else {
      throw new Error('تنسيق البيانات غير مدعوم');
    }

    projectData.createdAt = projectInfo.createdAt;
    projectData.lastModified = projectInfo.lastModified;

    saveImportedProject(newProjectId, projectInfo, projectData);

    return {
      success: true,
      message: 'تم استيراد المشروع (النسخة السريعة) بنجاح',
      projectId: newProjectId,
      projectInfo: projectInfo,
      projectData: projectData,
      type: 'quick'
    };

  } catch (error) {
    return {
      success: false,
      message: `فشل في الاستيراد السريع: ${error.message}`
    };
  }
};

// تحويل البيانات القديمة
const convertLegacyData = (oldData) => {
  const defaultProject = createDefaultProject();
  
  // نسخ البيانات القديمة وتحويلها
  if (oldData.elements) {
    defaultProject.pagesById.floor.elements = oldData.elements;
  }
  
  if (oldData.layers) {
    defaultProject.pagesById.floor.layers = oldData.layers;
  }
  
  if (oldData.history) {
    defaultProject.pagesById.floor.history = oldData.history;
  }
  
  // إعدادات أخرى
  if (typeof oldData.snapToGrid === 'boolean') {
    defaultProject.snapToGrid = oldData.snapToGrid;
  }
  
  if (oldData.gridSize) {
    defaultProject.gridSize = oldData.gridSize;
  }

  return defaultProject;
};

// إنشاء مشروع افتراضي
const createDefaultProject = () => {
  return {
    pagesById: {
      floor: {
        id: 'floor',
        label: 'أرضية',
        kind: 'floor',
        elements: [],
        layers: {
          layer1: { name: 'الطبقة الأساسية', visible: true, color: '#3498db' },
          layer2: { name: 'طبقة الخطوط', visible: true, color: '#e74c3c' },
          layer3: { name: 'طبقة النصوص', visible: true, color: '#FF9800' },
          measurements: { name: 'طبقة القياسات', visible: true, color: '#9b59b6' }
        },
        selectedLayer: 'layer1',
        history: [[]],
        historyIndex: 0,
        zoom: 1,
        pan: { x: 0, y: 0 }
      },
      ceiling: {
        id: 'ceiling',
        label: 'سقف',
        kind: 'ceiling',
        elements: [],
        layers: {
          layer1: { name: 'الطبقة الأساسية', visible: true, color: '#3498db' },
          layer2: { name: 'طبقة الخطوط', visible: true, color: '#e74c3c' },
          layer3: { name: 'طبقة النصوص', visible: true, color: '#FF9800' },
          measurements: { name: 'طبقة القياسات', visible: true, color: '#9b59b6' }
        },
        selectedLayer: 'layer1',
        history: [[]],
        historyIndex: 0,
        zoom: 1,
        pan: { x: 0, y: 0 }
      }
    },
    pageOrder: ['floor', 'ceiling'],
    activePageId: 'floor',
    snapToGrid: true,
    gridSize: 5,
    selectedAngleDirection: 'horizontal_right_up',
    projectInfo: {
      projectNumber: '',
      clientName: '',
      address: '',
      phone: '',
      measuredBy: '',
      branch: ''
    },
    devices: [],
    deviceNote: ''
  };
};

// حفظ المشروع المستورد
const saveImportedProject = (projectId, projectInfo, projectData) => {
  // حفظ بيانات المشروع
  localStorage.setItem(`cad_project:${projectId}`, JSON.stringify(projectData));
  
  // تحديث قائمة المشاريع
  const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
  projectsList.push(projectInfo);
  localStorage.setItem('cad_projects_list', JSON.stringify(projectsList));
  
  // تحديد المشروع كنشط
  localStorage.setItem('cad_last_project_id', projectId);
};